<template>
  <footer class="footer">
    <div class="inner">
      <div class="footer-wrap">
        <!-- 左侧：品牌 + 关于我们（上） + 产品体系（下） -->
        <div class="left-section">
          <!-- 品牌信息 -->
          <div class="brand-info">
            <div class="brand">{{ companyName }}</div>
            <p class="slogan">{{ slogan }}</p>
          </div>

          <!-- 关于我们 -->
          <div class="nav-item">
            <div class="title">关于我们</div>
            <ul class="links-row">
              <li>
                <RouterLink to="/about?tab=about">公司简介</RouterLink>
              </li>
              <li>
                <RouterLink to="/about?tab=honor">荣誉资质</RouterLink>
              </li>
              <li>
                <RouterLink to="/about?tab=contact">联系我们</RouterLink>
              </li>
              <li>
                <RouterLink to="/about?tab=join">加入我们</RouterLink>
              </li>
            </ul>
          </div>

          <!-- 产品与体系 -->
          <div class="nav-item">
            <div class="title">产品与体系</div>
            <ul class="links-row">
              <li>
                <RouterLink to="/">智慧水务</RouterLink>
              </li>
              <li>
                <RouterLink to="/">智慧水利</RouterLink>
              </li>
              <li>
                <RouterLink to="/">智慧环保</RouterLink>
              </li>
              <li>
                <!-- <RouterLink to="/" :class="{ active: isRouteActive('/') }">智慧农业</RouterLink> -->
                <RouterLink to="/">智慧农业</RouterLink>
              </li>
            </ul>
          </div>
        </div>

        <!-- 右侧：联系我们 + 二维码 -->
        <div class="right-section">
          <div class="contact-col">
            <div class="title">联系我们</div>
            <ul class="contact">
              <li>
                <span class="icon phone-icon"></span>
                电话：{{ hotline }}
              </li>
              <li>
                <span class="icon email-icon"></span>
                邮箱：{{ email }}
              </li>
              <li>
                <span class="icon address-icon"></span>
                地址：{{ address }}
              </li>
            </ul>
          </div>

          <div class="qrcode-col">
            <div
              class="qr"
              :style="{ backgroundImage: qrUrl ? `url(${qrUrl})` : '' }"
              aria-label="微信公众号二维码"
            ></div>
          </div>
        </div>
      </div>

      <div class="copyright">
        <div class="copyright-main">
          © {{ year }} {{ companyName }} · 网站：
          <template v-if="website">
            <a :href="website" target="_blank" rel="noopener">
              {{ domainFrom(website) }}
            </a>
          </template>
          <template v-else>
            <span>zbhj-tech.com</span>
          </template>
        </div>

        <div class="icp-info">
          <span class="icp-label">ICP备案：</span>
          <template v-if="icpUrl && icp">
            <a :href="icpUrl" target="_blank" rel="noopener" class="icp-link">{{ icp }}</a>
          </template>
          <template v-else>
            <span class="icp-text">{{ icp || '皖ICP备2025099605' }}</span>
          </template>

          <span v-if="policeIcp" class="icp-separator">|</span>
          <span v-if="policeIcp" class="icp-label">公安备案：</span>
          <template v-if="policeIcp && policeIcpUrl">
            <a :href="policeIcpUrl" target="_blank" rel="noopener" class="icp-link">{{ policeIcp }}</a>
          </template>
          <template v-else-if="policeIcp">
            <span class="icp-text">{{ policeIcp }}</span>
          </template>
          <template v-else>
            <span class="icp-text">皖公网安备34019202002657号</span>
          </template>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { useSiteConfig } from '@/data/useSiteConfig';
import { toPublicImagePath } from '@/data/imagePath';

const route = useRoute()
const { siteConfig } = useSiteConfig()

function isRouteActive(path: string) {
  return route.path === path
}

const companyName = computed(() => siteConfig.value.contact?.companyName || '安徽中标环境科技有限公司')
const slogan = computed(() => siteConfig.value.contact?.slogan || '专注水利与水务数字化建设，提供规划咨询、工程设计、智慧平台及运维保障。')
const hotline = computed(() => siteConfig.value.contact?.phone || '0551-65589899')
const email = computed(() => siteConfig.value.contact?.email || 'contact2026@zbhj-tech.com')
const address = computed(() => siteConfig.value.contact?.address || '安徽省合肥市高新区长宁社区服务中心创新大道2800号创新产业园二期F5栋')
const website = computed(() => siteConfig.value.contact?.website || 'https://zbhj-tech.com')
const icp = computed(() => siteConfig.value.icp || '皖ICP备2025099605')
const icpUrl = computed(() => siteConfig.value.icpUrl || '')
const policeIcp = computed(() => siteConfig.value.policeIcp || '皖公网安备34019202002657号')
const policeIcpUrl = computed(() => siteConfig.value.policeIcpUrl || '')
const qrUrl = computed(() => {
  const raw = siteConfig.value.wechatQrUrl || ''
  return raw ? toPublicImagePath(raw) : ''
})
const year = new Date().getFullYear()

function domainFrom(url: string) {
  try {
    const u = new URL(url)
    return u.host
  } catch {
    return url.replace(/^https?:\/\//, '').replace(/\/$/, '')
  }
}
</script>

<style lang="scss" scoped>
.footer {
  background: #25282B;
  line-height: 1.6;

  .inner {
    width: min(1500px, 94%);
    margin: 0 auto;
    padding: 2% 0;
  }

  /* 主布局：左右两栏 */
  .footer-wrap {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 40px;
    align-items: start;
    margin-bottom: 30px;
  }

  /* 左侧：品牌 + 导航（上下排列） */
  .left-section {
    display: flex;
    flex-direction: column;
    gap: 26px;

    .brand-info {
      margin-bottom: 8px;

      .brand {
        font-weight: 600;
        font-size: 18px;
        color: #FFFFFF;
        margin-bottom: 12px;
      }

      .slogan {
        font-weight: 400;
        font-size: 15px;
        color: #FFFFFF;
        margin: 0;
        line-height: 1.8;
      }
    }

    /* 每个导航块：标题 + 横向链接 */
    .nav-item {
      display: flex;
      flex-direction: column;
      gap: 12px;

      .title {
        font-family: PingFangSC, PingFang SC;
        font-weight: 400;
        font-size: 15px;
        color: #FFFFFF;
        line-height: 21px;
        text-align: left;
        font-style: normal;
        white-space: nowrap;
        margin: 0;
      }

      /* 横向链接样式 */
      .links-row {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        gap: 54px;
        flex-wrap: wrap;

        li {
          margin: 0;
          font-family: PingFangSC, PingFang SC;
          font-weight: 400;
          font-size: 15px;
          color: #ADADAD;
          line-height: 21px;
          text-align: left;
          font-style: normal;
        }

        a {
          color: #ADADAD;
          text-decoration: none;
          transition: color 0.2s;

          &:hover {
            color: #409eff;
          }

          &.active {
            color: #409eff;
            position: relative;

            &::after {
              content: '';
              position: absolute;
              left: 0;
              bottom: -6px;
              width: 100%;
              height: 3px;
              background: #409eff;
              border-radius: 2px;
            }
          }
        }
      }
    }
  }

  /* 右侧：联系我们 + 二维码 */
  .right-section {
    display: flex;
    gap: 32px;
    padding-top: 108px;

    .contact-col {
      display: flex;
      gap: 20px;

      .title {
        white-space: nowrap;
        font-family: PingFangSC, PingFang SC;
        font-weight: 400;
        font-size: 15px;
        color: #FFFFFF;
      }

      .contact {
        list-style: none;
        padding: 0;
        margin: 0;

        li {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          font-family: PingFangSC, PingFang SC;
          font-weight: 400;
          font-size: 15px;
          color: #ADADAD;
          line-height: 21px;
          text-align: left;
          font-style: normal;

          &:nth-child(2) {
            margin: 24px 0;
          }
        }
      }
    }

    .qrcode-col {
      .qr {
        width: 120px;
        height: 120px;
        background-position: center;
        background-size: cover;
        background-repeat: no-repeat;
        background-color: #ffffff;
        border-radius: 4px;
        margin-top: 12px;
      }
    }
  }

  .icon {
    display: inline-block;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    margin-top: 2px;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;

    &.phone-icon {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23e0e0e0'%3E%3Cpath d='M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z'/%3E%3C/svg%3E");
    }

    &.email-icon {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23e0e0e0'%3E%3Cpath d='M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z'/%3E%3C/svg%3E");
    }

    &.address-icon {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23e0e0e0'%3E%3Cpath d='M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z'/%3E%3C/svg%3E");
    }
  }

  .copyright {
    border-top: 1px solid #444444;
    padding-top: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    font-family: PingFangSC, PingFang SC;
    font-weight: 400;
    font-size: 12px;
    color: #E1E1E1;
    line-height: 17px;
    text-align: left;
    font-style: normal;

    .copyright-main {
      a {
        color: #e0e0e0;
        text-decoration: none;

        &:hover {
          color: #409eff;
        }
      }
    }

    .icp-info {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
      align-items: center;

      .icp-label {
        color: #cccccc;
        font-weight: 500;
      }

      .icp-link {
        color: #e0e0e0;
        text-decoration: none;

        &:hover {
          color: #409eff;
        }
      }
    }
  }
}

/* 响应式 */
@media (max-width: 1200px) {
  .footer-wrap {
    grid-template-columns: 2.5fr 1fr;
    gap: 32px;
  }
}

@media (max-width: 992px) {
  .footer-wrap {
    grid-template-columns: 1fr;
    gap: 28px;
  }

  .right-section {
    padding-top: 0 !important;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
  }
}

@media (max-width: 576px) {
  .right-section {
    flex-direction: column;
    gap: 24px;
  }

  .links-row {
    gap: 16px;
  }
}
</style>